import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Container,
  Link,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useAuth } from '../../contexts/AuthContext';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import GoogleIcon from '@mui/icons-material/Google';
import { useGoogleLogin } from '@react-oauth/google';
import SocialRegistrationModal from '../../components/SocialRegistrationModal';
import { validateCPFOrCNPJ, formatCPFOrCNPJ } from '../../utils/cpfCnpjValidator';

// Validation schema
const validationSchema = yup.object({
  nome_completo: yup
    .string()
    .required('Nome completo é obrigatório'),
  email: yup
    .string()
    .email('Digite um email válido')
    .required('Email é obrigatório'),
  cpf_cnpj: yup
    .string()
    .test('cpf-cnpj-valid', 'CPF ou CNPJ inválido', function(value) {
      return !value || validateCPFOrCNPJ(value); // Válido se vazio ou se válido
    }),
  numero_oab: yup
    .string()
    .matches(/^\d*$/, 'Número da OAB deve conter apenas números'),
  estado_oab: yup
    .string()
    .when('numero_oab', {
      is: (val: string) => val && val.length > 0,
      then: (schema) => schema.required('Estado da OAB é obrigatório quando número é fornecido'),
      otherwise: (schema) => schema
    }),
  phone: yup
    .string()
    .matches(/^[\d\s\(\)\-\+]*$/, 'Formato de telefone inválido'),
  password: yup
    .string()
    .min(6, 'A senha deve ter pelo menos 6 caracteres')
    .required('Senha é obrigatória'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'As senhas não coincidem')
    .required('Confirmação de senha é obrigatória')
});

const RegisterPage: React.FC = () => {
  const { register, loginWithGoogle, error, loading, clearError } = useAuth();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [isGoogleLoginDisabled, setIsGoogleLoginDisabled] = useState(false);
  
  // Estados para registro social
  const [socialModalOpen, setSocialModalOpen] = useState(false);
  const [socialUserInfo, setSocialUserInfo] = useState<{
    email: string;
    firstName: string;
    lastName: string;
    provider: 'google';
    token: string;
  } | null>(null);

  // Detectar usuário social vindo do login e abrir modal automaticamente
  useEffect(() => {
    const socialUser = location.state?.socialUser;
    if (socialUser) {
      setSocialUserInfo({
        email: socialUser.email,
        firstName: socialUser.firstName,
        lastName: socialUser.lastName,
        provider: socialUser.provider,
        token: '' // Token já foi salvo no AuthContext
      });
      setSocialModalOpen(true);
    }
  }, [location.state]);

  // Form handling with Formik
  const formik = useFormik({
    initialValues: {
      nome_completo: '',
      email: '',
      cpf_cnpj: '',
      numero_oab: '',
      estado_oab: '',
      phone: '',
      password: '',
      confirmPassword: ''
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        // Limpar erro do contexto antes de iniciar
        clearError();
        
        const { confirmPassword, ...userData } = values;
        
        console.log('Enviando dados de registro:', userData);
        
        // Mostra o indicador de carregamento
        setSuccess(false);
        
        await register(userData);
        
        // Se chegou aqui sem exceções, o registro foi bem-sucedido
        setSuccess(true);
        console.log('Registro bem-sucedido!');
      } catch (error) {
        console.error('Erro no formulário de registro:', error);
        setSuccess(false);
      }
    },
  });

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleClickShowConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };
  
  // Configuração do login com Google
  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      if (!isGoogleLoginDisabled) {
        try {
          // Para implicit flow, acessar token diretamente
          const accessToken = 'access_token' in tokenResponse ? tokenResponse.access_token : '';
          if (!accessToken) {
            throw new Error('Token de acesso não encontrado');
          }

          // Buscar dados do usuário do Google
          const userInfoResponse = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
            headers: {
              'Authorization': `Bearer ${accessToken}`
            }
          });
          
          if (userInfoResponse.ok) {
            const userInfo = await userInfoResponse.json();
            
            // Abrir modal para completar perfil
            setSocialUserInfo({
              email: userInfo.email,
              firstName: userInfo.given_name || '',
              lastName: userInfo.family_name || '',
              provider: 'google',
              token: accessToken
            });
            setSocialModalOpen(true);
          } else {
            throw new Error('Falha ao obter dados do usuário do Google');
          }
        } catch (error) {
          console.error('Erro no registro com Google:', error);
          // Fallback para o método antigo se houver token
          const accessToken = 'access_token' in tokenResponse ? tokenResponse.access_token : '';
          if (accessToken) {
            await loginWithGoogle(accessToken);
          }
        }
      }
    },
    onError: (error) => {
      console.error('Erro na autenticação com Google:', error);
    },
    // Usar implicit flow - já abre em popup por padrão
  });


  // Função para completar perfil social
  const handleCompleteSocialProfile = async (profileData: any) => {
    if (!socialUserInfo) return;

    try {
      // Usar o novo endpoint para completar perfil social
      const response = await fetch('/api/v1/auth/social/complete-profile', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...profileData,
          token: socialUserInfo.token,
          provider: socialUserInfo.provider
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        // Salvar token e dados do usuário
        localStorage.setItem('token', data.access_token);
        
        // Redirecionar para dashboard
        window.location.href = '/dashboard';
      } else {
        // Fallback para método antigo com Google
        await loginWithGoogle(socialUserInfo.token);
      }
    } catch (error) {
      console.error('Erro ao completar perfil social:', error);
      // Fallback para método antigo com Google
      await loginWithGoogle(socialUserInfo.token);
    }
  };

  // Fechar modal social
  const handleCloseSocialModal = () => {
    setSocialModalOpen(false);
    setSocialUserInfo(null);
  };



  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 8 }}>
        <Box component="form" onSubmit={formik.handleSubmit} noValidate>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            Cadastro
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Cadastro realizado com sucesso! Redirecionando...
            </Alert>
          )}

          <Box
            sx={{
              mt: 3,
              mb: 3,
              p: { xs: 2, sm: 2.5 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 3,
              bgcolor: 'grey.50',
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ mb: 1.5 }}
            >
              Cadastro mais rápido
            </Typography>
            <Button
              fullWidth
              variant="contained"
              color="inherit"
              startIcon={<GoogleIcon />}
              onClick={() => googleLogin()}
              sx={{
                py: 1.4,
                borderRadius: 2.5,
                textTransform: 'none',
                fontSize: '1rem',
                fontWeight: 600,
                bgcolor: '#ffffff',
                color: 'text.primary',
                border: '1px solid',
                borderColor: 'divider',
                boxShadow: '0 6px 18px rgba(15, 23, 42, 0.08)',
                '&:hover': {
                  bgcolor: '#ffffff',
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.12)',
                },
              }}
              disabled={loading}
            >
              Continuar com Google
            </Button>
          </Box>

          <Divider sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              ou cadastre com email
            </Typography>
          </Divider>
          
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="nome_completo"
                label="Nome Completo"
                name="nome_completo"
                autoComplete="name"
                autoFocus
                value={formik.values.nome_completo}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.nome_completo && Boolean(formik.errors.nome_completo)}
                helperText={formik.touched.nome_completo && formik.errors.nome_completo}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                required
                fullWidth
                id="email"
                label="Email"
                name="email"
                autoComplete="email"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                disabled={loading}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                name="password"
                label="Senha"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="new-password"
                value={formik.values.password}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                name="confirmPassword"
                label="Confirmar Senha"
                type={showConfirmPassword ? 'text' : 'password'}
                id="confirmPassword"
                autoComplete="new-password"
                value={formik.values.confirmPassword}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
                helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
                disabled={loading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowConfirmPassword}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ pt: 1.5 }}>
                <Divider sx={{ mb: 1.5 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                  Dados opcionais
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Você pode preencher depois, se quiser.
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                id="cpf_cnpj"
                label="CPF ou CNPJ (opcional)"
                name="cpf_cnpj"
                value={formik.values.cpf_cnpj}
                onChange={(e) => {
                  // Formatar automaticamente enquanto digita
                  const formatted = formatCPFOrCNPJ(e.target.value);
                  formik.setFieldValue('cpf_cnpj', formatted);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.cpf_cnpj && Boolean(formik.errors.cpf_cnpj)}
                helperText={formik.touched.cpf_cnpj && formik.errors.cpf_cnpj}
                disabled={loading}
                placeholder="000.000.000-00 ou 00.000.000/0000-00"
                inputProps={{ maxLength: 18 }}
              />
            </Grid>
            
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Número da OAB (opcional)"
                name="numero_oab"
                value={formik.values.numero_oab}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.numero_oab && Boolean(formik.errors.numero_oab)}
                helperText={
                  formik.touched.numero_oab && formik.errors.numero_oab ||
                  'Para acesso a funcionalidades exclusivas'
                }
                disabled={loading}
                placeholder="123456"
              />
            </Grid>

            {formik.values.numero_oab && (
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="Estado OAB"
                  name="estado_oab"
                  value={formik.values.estado_oab}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.estado_oab && Boolean(formik.errors.estado_oab)}
                  helperText={formik.touched.estado_oab && formik.errors.estado_oab}
                  SelectProps={{
                    native: true,
                  }}
                  disabled={loading}
                  InputLabelProps={{
                    shrink: true,
                  }}
                >
                  <option value=""></option>
                  {['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'].map((estado) => (
                    <option key={estado} value={estado}>
                      {estado}
                    </option>
                  ))}
                </TextField>
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Telefone (opcional)"
                name="phone"
                value={formik.values.phone}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={
                  formik.touched.phone && formik.errors.phone ||
                  'Para contato e recuperação de conta'
                }
                disabled={loading}
                placeholder="(11) 99999-9999"
              />
            </Grid>
          </Grid>
          
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : "Cadastrar"}
          </Button>

          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Link component={RouterLink} to="/login" variant="body2">
              Já tem uma conta? Faça login
            </Link>
          </Box>
        </Box>
      </Paper>

      {/* Modal de completar perfil social */}
      {socialUserInfo && (
        <SocialRegistrationModal
          open={socialModalOpen}
          onClose={handleCloseSocialModal}
          onComplete={handleCompleteSocialProfile}
          userInfo={socialUserInfo}
          loading={loading}
          error={error}
        />
      )}
    </Container>
  );
};

export default RegisterPage; 